export type Reminder = {
  id: string;
  title: string;
  body?: string;
  atISO: string; // ISO datetime
  delivered?: boolean;
  createdAtISO: string;
};

const STORAGE_KEY = 'hipo_reminders_v1';
let intervalId: number | null = null;

function loadReminders(): Reminder[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveReminders(list: Reminder[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

export async function ensurePermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission !== 'denied') {
    try {
      const perm = await Notification.requestPermission();
      return perm;
    } catch {
      return Notification.permission;
    }
  }
  return Notification.permission;
}

function deliver(rem: Reminder) {
  if ('Notification' in window && Notification.permission === 'granted') {
    try { new Notification(rem.title, { body: rem.body }); } catch {}
  } else {
    // Fallback UI
    alert(`${rem.title}${rem.body ? '\n\n' + rem.body : ''}`);
  }
}

function tick() {
  const now = Date.now();
  const list = loadReminders();
  let changed = false;
  list.forEach((r) => {
    if (!r.delivered) {
      const at = new Date(r.atISO).getTime();
      if (isFinite(at) && at <= now) {
        deliver(r);
        r.delivered = true;
        changed = true;
      }
    }
  });
  if (changed) saveReminders(list);
}

export function initReminders() {
  if (intervalId) return; // already initialized
  // Run immediate sweep, then every 30s
  tick();
  intervalId = window.setInterval(tick, 30 * 1000);
}

export async function scheduleReminder(args: { title: string; body?: string; at: Date; id?: string }) {
  const perm = await ensurePermission();
  if (perm === 'denied') {
    // still store and fallback to alert later
  }
  const list = loadReminders();
  const id = args.id || `rem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const rem: Reminder = {
    id,
    title: args.title,
    body: args.body,
    atISO: args.at.toISOString(),
    delivered: false,
    createdAtISO: new Date().toISOString(),
  };
  list.push(rem);
  saveReminders(list);
  // If the reminder is within 30s, attempt immediate delivery when tick runs
  tick();
  return rem;
}
